import { useReducer, useRef } from "react";
import { randomString } from "hooks/helper";

var session = null;

let internalDispatch;
let internalDbDispatch;
let internalDb;

const timestamp = (pref1, pref2, pref3) => {
  if (pref1 && pref1 !== "") {
    return pref1;
  } else if (pref2 && pref2 !== "") {
    return pref2;
  } else if (pref3 && pref3 !== "") {
    return pref3;
  }
  return "";
};

const mediaConstraints = {
  audio: {
    sampleRate: 2800,
  }, // We want an audio track
  video: {
    width: { min: 70, ideal: 180, max: 360 },
    height: { min: 70, ideal: 180, max: 360 },
    frameRate: 5,
  }, // ...and we want a video track
};

// https://webrtchacks.com/limit-webrtc-bandwidth-sdp/
const setMediaBitrates = (sdp) => {
  return setMediaBitrate(setMediaBitrate(sdp, "video", 50), "audio", 8);
};

const setMediaBitrateFF = (sdp, mediaType, bitrate) => {
  let sdpLines = sdp.split("\n"),
    // mediaLineIndex = -1,
    mediaLine = "m=" + mediaType,
    bitrateLineIndex = -1,
    bitrateLine = "b=AS:" + bitrate,
    mediaLineIndex = sdpLines.findIndex((line) => line.startsWith(mediaLine));

  // If we find a line matching “m={mediaType}”
  if (mediaLineIndex && mediaLineIndex < sdpLines.length) {
    // Skip the media line
    bitrateLineIndex = mediaLineIndex + 1;

    // Skip both i=* and c=* lines (bandwidths limiters have to come afterwards)
    while (
      sdpLines[bitrateLineIndex].startsWith("i=") ||
      sdpLines[bitrateLineIndex].startsWith("c=")
    ) {
      bitrateLineIndex++;
    }

    if (sdpLines[bitrateLineIndex].startsWith("b=")) {
      // If the next line is a b=* line, replace it with our new bandwidth
      sdpLines[bitrateLineIndex] = bitrateLine;
    } else {
      // Otherwise insert a new bitrate line.
      sdpLines.splice(bitrateLineIndex, 0, bitrateLine);
    }
  }

  // Then return the updated sdp content as a string
  return sdpLines.join("\n");
};

const setMediaBitrate = (sdp, media, bitrate) => {
  var lines = sdp.split("\n");
  var line = -1;
  for (var i = 0; i < lines.length; i++) {
    if (lines[i].indexOf("m=" + media) === 0) {
      line = i;
      break;
    }
  }
  if (line === -1) {
    console.debug("Could not find the m line for", media);
    return sdp;
  }
  console.debug("Found the m line for", media, "at line", line);

  // Pass the m line
  line++;

  // Skip i and c lines
  while (lines[line].indexOf("i=") === 0 || lines[line].indexOf("c=") === 0) {
    line++;
  }

  // If we're on a b line, replace it
  if (lines[line].indexOf("b") === 0) {
    console.debug("Replaced b line at line", line);
    lines[line] = "b=AS:" + bitrate;
    return lines.join("\n");
  }

  // Add a new b line
  console.debug("Adding new b line before line", line);
  var newLines = lines.slice(0, line);
  newLines.push("b=AS:" + bitrate);
  newLines = newLines.concat(lines.slice(line, lines.length));
  return newLines.join("\n");
};

const closeVideoCall = () => {
  var remoteVideo = document.getElementById("received_video");
  var localVideo = document.getElementById("local_video");

  if (session) {
    session.ontrack = null;
    session.onremovetrack = null;
    session.onremovestream = null;
    session.onicecandidate = null;
    session.oniceconnectionstatechange = null;
    session.onsignalingstatechange = null;
    session.onicegatheringstatechange = null;
    session.onnegotiationneeded = null;

    if (remoteVideo.srcObject) {
      remoteVideo.srcObject.getTracks().forEach((track) => track.stop());
    }

    if (localVideo.srcObject) {
      localVideo.srcObject.getTracks().forEach((track) => track.stop());
    }

    session.close();
    session = null;
  }

  remoteVideo.removeAttribute("src");
  remoteVideo.removeAttribute("srcObject");
  localVideo.removeAttribute("src");
  localVideo.removeAttribute("srcObject");

  document.getElementById("hangup-button").disabled = true;
};

const handleGetUserMediaError = (e) => {
  switch (e.name) {
    case "NotFoundError":
      alert(
        "Unable to open your call because no camera and/or microphone" +
          "were found."
      );
      break;
    case "SecurityError":
    case "PermissionDeniedError":
      // Do nothing; this is the same as the user canceling the call.
      break;
    default:
      alert("Error opening your camera and/or microphone: " + e.message);
      break;
  }
};

const createPeerConnection = (
  sid,
  mpersona,
  peermpersona,
  db,
  databaseDispatch,
  version
) => {
  //   alert("Session ID " + sid);

  internalDbDispatch = databaseDispatch;
  internalDb = db;

  session = new RTCPeerConnection({
    iceServers: [
      // Information about ICE servers - Use your own!
      {
        urls: "stun:stun.stunprotocol.org",
      },
    ],
  });

  const handleICECandidateEvent = (event) => {
    if (event.candidate) {
      let j = {
        version: version,
        sid: sid,
        type: "w.mpersona.stream.new_ice_candidate",
        mpersona: mpersona,
        peer: peermpersona,
        candidate: event.candidate,
        smid: randomString(8),
        ts_sender: "",
      };
      console.log("useWebRTCState handleICECandidateEvent ", j, session);
      db &&
        databaseDispatch({
          type: "UPSERT_REC",
          values: {
            jmessage: j,
            db: db,
            collection: "send",
            table: "confirm_smid",
            rowid: j.smid,
            doc: j,
          },
        });
    }
  };

  const handleICEConnectionStateChangeEvent = (event) => {
    console.log(
      "useWebRTCState handleICEConnectionStateChangeEvent ",
      event,
      session
    );
    switch (session.iceConnectionState) {
      case "closed":
      case "failed":
        closeVideoCall();
        break;
      default:
        break;
    }
  };

  const handleTrackEvent = (event) => {
    console.log("useWebRTCState handleTrackEvent ", event, session);
    document.getElementById("received_video").srcObject = event.streams[0];
    document.getElementById("hangup-button").disabled = false;
  };

  const handleNegotiationNeededEvent = () => {
    session
      .createOffer()
      .then((offer) => {
        // console.debug("pre_mod_sdp: ", offer.sdp);
        // offer.sdp = setMediaBitrates(offer.sdp);
        // console.debug("post_mod_sdp: ", offer.sdp);
        return session.setLocalDescription(offer);
      })
      .then(() => {
        let j = {
          version: version,
          sid: sid,
          type: "w.mpersona.stream.offer",
          mpersona: mpersona,
          peer: peermpersona,
          sdp: session.localDescription,
          smid: randomString(8),
          ts_sender: "",
        };
        console.log("useWebRTCState handleNegotiationNeededEvent ", j, session);
        db &&
          databaseDispatch({
            type: "UPSERT_REC",
            values: {
              jmessage: j,
              db: db,
              collection: "send",
              table: "confirm_smid",
              rowid: j.smid,
              doc: j,
            },
          });
      });
  };

  const handleRemoveTrackEvent = (event) => {
    var stream = document.getElementById("received_video").srcObject;
    var trackList = stream.getTracks();
    console.log("useWebRTCState handleRemoveTrackEvent ", event, session);
    if (trackList.length === 0) {
      closeVideoCall();
    }
  };

  const handleICEGatheringStateChangeEvent = (event) => {};

  const handleSignalingStateChangeEvent = (event) => {
    console.log(
      "useWebRTCState handleSignalingStateChangeEvent ",
      event,
      session
    );
    switch (session.signalingState) {
      case "closed":
        closeVideoCall();
        break;
      default:
        break;
    }
  };

  session.onicecandidate = handleICECandidateEvent;
  session.ontrack = handleTrackEvent;
  session.onnegotiationneeded = handleNegotiationNeededEvent;
  session.onremovetrack = handleRemoveTrackEvent;
  session.oniceconnectionstatechange = handleICEConnectionStateChangeEvent;
  session.onicegatheringstatechange = handleICEGatheringStateChangeEvent;
  session.onsignalingstatechange = handleSignalingStateChangeEvent;
};

const handleVideoOfferMsg = (
  sid,
  sdp,
  mpersona,
  peermpersona,
  db,
  databaseDispatch,
  version
) => {
  internalDbDispatch = databaseDispatch;
  internalDb = db;
  var localStream = null;
  // have - remote - offer;
  // have - local - preanswer;
  createPeerConnection(
    sid,
    mpersona,
    peermpersona,
    db,
    databaseDispatch,
    version
  );

  var desc = new RTCSessionDescription(sdp);

  session &&
    session
      .setRemoteDescription(desc)
      .then(function () {
        return navigator.mediaDevices.getUserMedia(mediaConstraints);
      })
      .then(function (stream) {
        console.log("pre answer stream", stream);
        // try {
        localStream = stream;
        document.getElementById("local_video").srcObject = localStream;

        localStream
          .getTracks()
          .forEach((track) => session.addTrack(track, localStream));
        // } catch {}
      })
      .then(function () {
        console.log("creating the answer");
        return session.createAnswer();
      })
      .then(function (answer) {
        // console.debug("pre_mod_sdp: ", answer.sdp);
        // answer.sdp = setMediaBitrates(answer.sdp);
        // console.debug("post_mod_sdp: ", answer.sdp);
        return session.setLocalDescription(answer);
      })
      .then(function () {
        let j = {
          version: version,
          type: "w.mpersona.stream.answer",
          sid: sid,
          mpersona: mpersona,
          peer: peermpersona,
          sdp: session.localDescription,
          smid: randomString(8),
          ts_sender: "",
        };

        console.log("useWebRTCState handleVideoOfferMsg ", j, session);
        db &&
          databaseDispatch({
            type: "UPSERT_REC",
            values: {
              jmessage: j,
              db: db,
              collection: "send",
              table: "confirm_smid",
              rowid: j.smid,
              doc: j,
            },
          });
      })
      .catch(handleGetUserMediaError);
};

const reducer = (state, action) => {
  let ts_server = "";
  switch (action.type) {
    case "close":
      console.log("!live close ", state);
      return {
        ...state,
        ts_server: "",
        sid: undefined,
      };

    case "invite":
      let sid = randomString(8);
      // state.session && (state.session = null);
      createPeerConnection(
        sid,
        action.values.mpersona,
        action.values.peermpersona,
        action.values.db,
        action.values.databaseDispatch,
        action.values.version
      );
      console.log("!live invite ", state, session);

      navigator.mediaDevices
        .getUserMedia(mediaConstraints)
        .then((localStream) => {
          document.getElementById("local_video").srcObject = localStream;
          localStream
            .getTracks()
            .forEach((track) => session.addTrack(track, localStream));

          console.log("useWebRTCState invite ", session);
        })
        .catch(handleGetUserMediaError, () => closeVideoCall());
      return {
        ...state,
        sid: sid,
      };

    case "w.mpersona.stream.offer":
      let rOffer;
      if (session && session.signalingState !== "closed") {
        rOffer = { ...state };
      } else {
        handleVideoOfferMsg(
          action.values.message.sid,
          action.values.message.sdp,
          action.values.mpersona,
          action.values.peermpersona,
          action.values.db,
          action.values.databaseDispatch,
          action.values.version
        );

        ts_server = timestamp(
          action.values.message.ts_server,
          action.values.message.ts_origin_server,
          action.values.message.ts_sender
        );

        rOffer = {
          ...state,
          sid: action.values.message.sid,
          ts_server: ts_server,
        };
      }

      console.log(
        "useWebRTCState w.mpersona.stream.offer ",
        action,
        state,
        rOffer
      );

      return rOffer;

    case "w.mpersona.stream.new_ice_candidate":
      let candidate = new RTCIceCandidate(action.values.message.candidate);
      ts_server = timestamp(
        action.values.message.ts_server,
        action.values.message.ts_origin_server,
        action.values.message.ts_sender
      );
      session &&
        state.sid === action.values.message.sid &&
        // ts_server > state.ts_server &&
        session.addIceCandidate(candidate).catch((e) => {});
      console.log(
        "useWebRTCState w.mpersona.stream.new_ice_candidate ",
        action,
        state
      );
      return {
        ...state,
        ts_server: ts_server,
      };

    case "w.mpersona.stream.hang_up":
      console.log("useWebRTCState w.mpersona.stream.hang_up ", action, state);
      // ts_server = timestamp(
      //   action.values.message.ts_server,
      //   action.values.message.ts_origin_server,
      //   action.values.message.ts_sender
      // );
      // state.sid === action.values.message.sid &&
      // ts_server > state.ts_server &&
      closeVideoCall();
      session = null;
      let rVal = {
        ...state,
        ts_server: "",
        sid: undefined,
      };
      console.log("!live hangup ", rVal);
      // internalDispatch({
      //   type: "close",
      //   values: {},
      // });

      internalDbDispatch &&
        internalDbDispatch({
          type: "DELETE_STREAM_MESSAGES",
          values: {
            db: internalDb,
          },
        });
      return rVal;

    case "w.mpersona.stream.hang_up.self":
      console.log(
        "useWebRTCState w.mpersona.stream.hang_up.self ",
        action,
        state
      );
      closeVideoCall();
      session = null;
      let rsVal = {
        ...state,
        ts_server: "",
        sid: undefined,
      };

      // internalDispatch({
      //   type: "close",
      //   values: {},
      // });

      // internalDbDispatch &&
      //   internalDbDispatch({
      //     type: "DELETE_STREAM_MESSAGES",
      //     db: internalDb,
      //     values: {},
      //   });

      console.log("!live hangup self ", rsVal);
      return rsVal;

    case "w.mpersona.stream.answer":
      let desc = new RTCSessionDescription(action.values.message.sdp);
      ts_server = timestamp(
        action.values.message.ts_server,
        action.values.message.ts_origin_server,
        action.values.message.ts_sender
      );
      session &&
        // state.sid === action.values.message.sid &&
        // ts_server > state.ts_server &&
        session.setRemoteDescription(desc);
      console.log("useWebRTCState w.mpersona.stream.answer ", action, state);
      return {
        ...state,
        ts_server: ts_server,
      };

    default: {
      console.log("useWebRTCState default ", action, state);
      return { ...state };
    }
  }
};

const useWebRTCState = (state) => {
  const [webRTCState, webRTCDispatch] = useReducer(reducer, state);
  internalDispatch = webRTCDispatch;
  return { webRTCState, webRTCDispatch };
};

export default useWebRTCState;
